import { ethers } from 'ethers';

export type ClaimFlow = 'enrollment' | 'direct_claim' | 'pattern_claim';

export type EnrollmentAuthorization = {
  wallet: string;
  nonce: string;
  deadline: number;
};

export type DirectClaimAuthorization = EnrollmentAuthorization & {
  token: string;
};

export type PatternClaimAuthorization = EnrollmentAuthorization & {
  patternId: string;
};

export type ClaimAuthorization =
  | EnrollmentAuthorization
  | DirectClaimAuthorization
  | PatternClaimAuthorization;

export type WorldIdSessionId = `session_${string}`;

export type ParsedClaimAuthorization = {
  flow: ClaimFlow;
  authorization: ClaimAuthorization;
  signature: string;
  authorizationDigest: string;
};

export type ClaimRecoveryRecord = ParsedClaimAuthorization & {
  version: 2;
  wallet: string;
  patternId: string | null;
  idempotencyKey: string;
  deadline: number;
};

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length?: number;
  key?(index: number): string | null;
}

const RECOVERY_VERSION = 2;
const SESSION_STORAGE_VERSION = 1;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function parseAddress(value: unknown, label: string): string {
  if (typeof value !== 'string' || !ethers.isAddress(value)) {
    throw new Error(`authorization.${label} 格式不正確。`);
  }
  return ethers.getAddress(value);
}

function parseBytes32(value: unknown, label: string): string {
  if (typeof value !== 'string' || !ethers.isHexString(value, 32)) {
    throw new Error(`authorization.${label} 必須是 bytes32。`);
  }
  return value.toLowerCase();
}

function parseDeadline(value: unknown): number {
  const deadline = typeof value === 'number'
    ? value
    : typeof value === 'string' && /^\d+$/.test(value)
      ? Number(value)
      : NaN;
  if (!Number.isSafeInteger(deadline) || deadline <= 0) {
    throw new Error('authorization.deadline 格式不正確。');
  }
  return deadline;
}

function parseSignature(value: unknown): string {
  if (
    typeof value !== 'string'
    || (!ethers.isHexString(value, 64) && !ethers.isHexString(value, 65))
  ) {
    throw new Error('authorization signature 格式不正確。');
  }
  return value;
}

function parseIdempotencyKey(value: unknown): string {
  if (typeof value !== 'string' || value.length < 16 || value.length > 128) {
    throw new Error('idempotency_key 格式不正確。');
  }
  return value;
}

export function parseClaimAuthorizationResponse(
  value: unknown,
  flow: ClaimFlow,
  expectedWallet: string,
  expectedPatternId?: string,
): ParsedClaimAuthorization {
  const root = asRecord(value);
  const rawAuthorization = root ? asRecord(root.authorization) : null;
  if (!root || !rawAuthorization) throw new Error('Bubble authorization 回應格式不正確。');

  const wallet = parseAddress(rawAuthorization.wallet, 'wallet');
  const normalizedExpectedWallet = parseAddress(expectedWallet, 'wallet').toLowerCase();
  if (wallet.toLowerCase() !== normalizedExpectedWallet) {
    throw new Error('authorization wallet 與目前 World App wallet 不一致。');
  }

  const nonce = parseBytes32(rawAuthorization.nonce, 'nonce');
  const deadline = parseDeadline(rawAuthorization.deadline);
  const signature = parseSignature(root.signature);
  const authorizationDigest = parseBytes32(root.authorization_digest, 'authorization_digest');

  if (flow === 'enrollment') {
    return {
      flow,
      authorization: { wallet, nonce, deadline },
      signature,
      authorizationDigest,
    };
  }

  if (flow === 'direct_claim') {
    return {
      flow,
      authorization: {
        wallet,
        token: parseAddress(rawAuthorization.token, 'token'),
        nonce,
        deadline,
      },
      signature,
      authorizationDigest,
    };
  }

  const patternId = parseBytes32(rawAuthorization.patternId, 'patternId');
  if (expectedPatternId && patternId !== parseBytes32(expectedPatternId, 'patternId')) {
    throw new Error('authorization patternId 與目前請求不一致。');
  }
  return {
    flow,
    authorization: { wallet, patternId, nonce, deadline },
    signature,
    authorizationDigest,
  };
}

export function createClaimRecoveryRecord(
  parsed: ParsedClaimAuthorization,
  idempotencyKey: string,
): ClaimRecoveryRecord {
  const wallet = parsed.authorization.wallet;
  const patternId = parsed.flow === 'pattern_claim'
    ? (parsed.authorization as PatternClaimAuthorization).patternId
    : null;
  return {
    ...parsed,
    version: RECOVERY_VERSION,
    wallet,
    patternId,
    idempotencyKey: parseIdempotencyKey(idempotencyKey),
    deadline: parsed.authorization.deadline,
  };
}

export function isAuthorizationExpired(deadline: number, nowSeconds = Math.floor(Date.now() / 1000)): boolean {
  return nowSeconds > deadline;
}

export function recoveryStorageKey(contractAddress: string): string {
  return `bubble-wallet:world-id-claim-recovery:v${RECOVERY_VERSION}:${contractAddress.toLowerCase()}`;
}

export function sessionStorageKey(contractAddress: string, wallet: string, rpId = 'default'): string {
  return `bubble-wallet:world-id-session:v${SESSION_STORAGE_VERSION}:${contractAddress.toLowerCase()}:${rpId.toLowerCase()}:${wallet.toLowerCase()}`;
}

function parseSessionId(value: unknown): WorldIdSessionId | null {
  return typeof value === 'string' && /^session_[0-9a-f]{128}$/i.test(value)
    ? value as WorldIdSessionId
    : null;
}

export function readWorldIdSessionId(
  storage: StorageLike,
  contractAddress: string,
  wallet: string,
  rpId = 'default',
): WorldIdSessionId | null {
  const key = sessionStorageKey(contractAddress, wallet, rpId);
  try {
    return parseSessionId(storage.getItem(key));
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function writeWorldIdSessionId(
  storage: StorageLike,
  contractAddress: string,
  wallet: string,
  sessionId: string,
  rpId = 'default',
): void {
  const parsed = parseSessionId(sessionId);
  if (!parsed) return;
  try {
    storage.setItem(sessionStorageKey(contractAddress, wallet, rpId), parsed);
  } catch {
    // Session continuity still works through the backend canonical lookup.
  }
}

export function clearWorldIdSessionId(
  storage: StorageLike,
  contractAddress: string,
  wallet?: string,
): void {
  const prefix = `bubble-wallet:world-id-session:v${SESSION_STORAGE_VERSION}:${contractAddress.toLowerCase()}:`;
  try {
    if (wallet) storage.removeItem(sessionStorageKey(contractAddress, wallet));
    if (typeof storage.length !== 'number' || typeof storage.key !== 'function') return;
    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(prefix)) keysToRemove.push(key);
    }
    for (const key of keysToRemove) storage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures during logout or wallet changes.
  }
}

export function selectWorldIdSessionId(
  canonicalSessionId: WorldIdSessionId | null,
  cachedSessionId: WorldIdSessionId | null,
  activeSessionId: WorldIdSessionId | null,
): WorldIdSessionId | null {
  return canonicalSessionId ?? cachedSessionId ?? activeSessionId;
}

export function computeClaimAuthorizationDigest(
  flow: ClaimFlow,
  authorization: ClaimAuthorization,
  chainId: number,
  contractAddress: string,
): string {
  const domain = {
    name: 'BubbleClaim',
    version: '1',
    chainId,
    verifyingContract: ethers.getAddress(contractAddress),
  };
  const types: Record<string, Array<{ name: string; type: string }>> = flow === 'enrollment'
    ? {
        EnrollmentAuthorization: [
          { name: 'wallet', type: 'address' },
          { name: 'nonce', type: 'bytes32' },
          { name: 'deadline', type: 'uint256' },
        ],
      }
    : flow === 'direct_claim'
      ? {
          DirectClaimAuthorization: [
            { name: 'wallet', type: 'address' },
            { name: 'token', type: 'address' },
            { name: 'nonce', type: 'bytes32' },
            { name: 'deadline', type: 'uint256' },
          ],
        }
      : {
          PatternClaimAuthorization: [
            { name: 'wallet', type: 'address' },
            { name: 'patternId', type: 'bytes32' },
            { name: 'nonce', type: 'bytes32' },
            { name: 'deadline', type: 'uint256' },
          ],
        };
  return ethers.TypedDataEncoder.hash(domain, types, authorization);
}

export function readClaimRecovery(
  storage: StorageLike,
  contractAddress: string,
  wallet: string,
  nowSeconds = Math.floor(Date.now() / 1000),
): ClaimRecoveryRecord | null {
  const key = recoveryStorageKey(contractAddress);
  let rawValue: unknown;
  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    rawValue = JSON.parse(serialized);
  } catch {
    storage.removeItem(key);
    return null;
  }

  const raw = asRecord(rawValue);
  if (!raw || raw.version !== RECOVERY_VERSION) {
    storage.removeItem(key);
    return null;
  }

  try {
    const flow = raw.flow;
    if (flow !== 'enrollment' && flow !== 'direct_claim' && flow !== 'pattern_claim') {
      throw new Error('invalid flow');
    }
    const parsed = parseClaimAuthorizationResponse(
      {
        authorization: raw.authorization,
        signature: raw.signature,
        authorization_digest: raw.authorizationDigest,
      },
      flow,
      wallet,
      typeof raw.patternId === 'string' ? raw.patternId : undefined,
    );
    const idempotencyKey = parseIdempotencyKey(raw.idempotencyKey);
    const record = createClaimRecoveryRecord(parsed, idempotencyKey);
    if (isAuthorizationExpired(record.deadline, nowSeconds)) {
      storage.removeItem(key);
      return null;
    }
    return record;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function writeClaimRecovery(storage: StorageLike, contractAddress: string, record: ClaimRecoveryRecord): void {
  try {
    storage.setItem(recoveryStorageKey(contractAddress), JSON.stringify({
      ...record,
      authorizationDigest: record.authorizationDigest,
    }));
  } catch {
    // A transaction can still be completed when browser storage is unavailable.
  }
}

export function clearClaimRecovery(storage: StorageLike, contractAddress: string): void {
  try {
    storage.removeItem(recoveryStorageKey(contractAddress));
  } catch {
    // Ignore storage cleanup failures during logout or wallet changes.
  }
}

export function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  return `bubble-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}
